import {
  Box,
  Flex,
  Heading,
  Icon,
  Image,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from "@chakra-ui/react";
import { FunctionComponent, useMemo, useState } from "react";
import { RiCalendarLine, RiFlaskLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { CustomSelect } from "src/components/Inputs/CustomSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useBranding } from "src/components/Providers/BrandingProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useGetPublicCapacityKey } from "src/generated/hooks/form-service-public/queries";
import * as FSPR from "src/generated/hooks/form-service-public/requests";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import AvelaLogo from "src/images/avela-logo-color.svg?react";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import { getWhiteLogo } from "src/schemas/Branding";
import { isNonEmptyString } from "src/services/predicates";
import { AvailabilityFilterBar } from "./AvailabilityFilterBar";
import { SchoolAccordion } from "./SchoolAccordion";
import {
  AvailabilityFilters,
  EMPTY_FILTERS,
  applyFilters,
  uniqueSortedGradeLabels,
  uniqueSortedValues,
} from "./filters";

const ENROLLMENT_PERIOD_PARAM = "enrollmentPeriod";

type EnrollmentPeriodItem = { key: string; label: string };

export const Availability: FunctionComponent<{}> = () => {
  const flags = useFlags(["avela-labs-capacity-availability"]);
  const isEnabled = flags["avela-labs-capacity-availability"].enabled;

  const branding = useBranding();
  const organizationPath = useOrganizationPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<AvailabilityFilters>(EMPTY_FILTERS);

  const selectedPeriod = searchParams.get(ENROLLMENT_PERIOD_PARAM) ?? undefined;

  const capacityRD = useRemoteDataRestQuery({
    enabled: isEnabled && isNonEmptyString(organizationPath),
    refetchOnWindowFocus: false,
    queryKey: [useGetPublicCapacityKey, organizationPath, selectedPeriod],
    queryFn: async () => {
      const response = await FSPR.getPublicCapacity({
        throwOnError: false,
        query: {
          organizationPath: organizationPath ?? "",
          ...(selectedPeriod === undefined
            ? {}
            : { enrollmentPeriodId: selectedPeriod }),
        },
      });

      if (response.error) {
        if (response.response.status === 404) {
          return null;
        }
        throw new Error(response.error.error.message, {
          cause: response.error,
        });
      }

      return response.data?.data ?? null;
    },
  });

  // Frontend Flagsmith kill switch. Backend also checks the same flag and
  // returns 404 when unpublished / disabled — mapped to NotFound below.
  if (!isEnabled) {
    return <NotFound />;
  }

  const logo = getWhiteLogo(branding);

  return (
    <Flex
      direction="column"
      minHeight="100vh"
      bg="white"
      className={WEGLOT_APPLY_CLASS}
    >
      <Flex
        as="header"
        width="100%"
        height="5.125rem"
        flex="none"
        backgroundColor="primary.500"
        align="center"
        paddingX={4}
        gap={4}
      >
        <Image
          src={logo.url}
          alt={logo.alt ?? "Logo"}
          height="3em"
          maxWidth="9rem"
        />
      </Flex>

      <RemoteDataView
        remoteData={capacityRD}
        loading={<Loading />}
        error={() => <GenericError />}
      >
        {(capacity) => {
          if (capacity === null) return <NotFound />;

          return (
            <AvailabilityContent
              capacity={capacity}
              filters={filters}
              onFiltersChange={setFilters}
              onEnrollmentPeriodChange={(enrollmentPeriodId) => {
                setFilters(EMPTY_FILTERS);
                setSearchParams(
                  { [ENROLLMENT_PERIOD_PARAM]: enrollmentPeriodId },
                  { replace: true }
                );
              }}
            />
          );
        }}
      </RemoteDataView>
    </Flex>
  );
};

type ContentProps = {
  capacity: FSPR.PublicCapacityResponse;
  filters: AvailabilityFilters;
  onFiltersChange: (filters: AvailabilityFilters) => void;
  onEnrollmentPeriodChange: (enrollmentPeriodId: string) => void;
};

const AvailabilityContent: FunctionComponent<ContentProps> = ({
  capacity,
  filters,
  onFiltersChange,
  onEnrollmentPeriodChange,
}) => {
  const gradeLabels = useMemo(
    () => uniqueSortedGradeLabels(capacity.rows),
    [capacity.rows]
  );
  const programLabels = useMemo(
    () => uniqueSortedValues(capacity.rows, (row) => row.programLabel),
    [capacity.rows]
  );
  const visibleRows = useMemo(
    () => applyFilters(capacity.rows, filters, capacity.seatLegend),
    [capacity.rows, capacity.seatLegend, filters]
  );
  const totalSchoolCount = useMemo(
    () => new Set(capacity.rows.map((row) => row.schoolId)).size,
    [capacity.rows]
  );
  const periodItems: EnrollmentPeriodItem[] = useMemo(
    () =>
      capacity.enrollmentPeriods.map((period) => ({
        key: period.id,
        label: period.name,
      })),
    [capacity.enrollmentPeriods]
  );
  const selectedPeriodItem =
    periodItems.find((item) => item.key === capacity.enrollmentPeriodId) ??
    null;

  return (
    <Flex
      direction="column"
      gap={6}
      width="100%"
      maxWidth="72rem"
      alignSelf="center"
      padding={{ base: 4, md: 8 }}
    >
      <Flex direction="column" gap={2} alignItems="flex-start">
        <Heading as="h1" size="lg" color="primary.700">
          {capacity.title}
        </Heading>
        {capacity.description === "" ? null : (
          <Text color="gray.700">{capacity.description}</Text>
        )}
      </Flex>

      {capacity.enrollmentPeriods.length > 1 ? (
        <Box width="100%">
          <CustomSelect<EnrollmentPeriodItem>
            label="Enrollment period"
            items={periodItems}
            selectedItem={selectedPeriodItem}
            onChange={(item) => {
              if (item !== null) onEnrollmentPeriodChange(item.key);
            }}
            renderItem={(item) => item.label}
            renderSelectedItem={(item) => item?.label ?? "Select a period"}
            leftIcon={<Icon as={RiCalendarLine} color="gray.400" />}
          />
        </Box>
      ) : null}

      <AvailabilityFilterBar
        filters={filters}
        onChange={onFiltersChange}
        gradeLabels={gradeLabels}
        programLabels={programLabels}
        showPrograms={capacity.showPrograms}
      />

      <SchoolAccordion
        rows={visibleRows}
        seatLegend={capacity.seatLegend}
        totalSchoolCount={totalSchoolCount}
        showPrograms={capacity.showPrograms}
        showWaitlist={capacity.showWaitlist}
      />

      <Text fontSize="sm" color="gray.500">
        Availability is shown as a general range and updates periodically. It
        does not guarantee a seat.
      </Text>

      <Flex alignItems="center" gap={2}>
        <Text
          fontSize="xs"
          display="flex"
          alignItems="center"
          gap={1}
          color="gray.600"
        >
          Powered by <AvelaLogo aria-label="Avela" />
        </Text>
        <Tag size="sm" variant="outline" colorScheme="purple">
          <TagLeftIcon as={RiFlaskLine} mr={1} />
          <TagLabel>Beta</TagLabel>
        </Tag>
      </Flex>
    </Flex>
  );
};
